
// Reference-plane fit for the "Areal og volumen" tool - see this repo's
// task-log for the full sanity-check this design was derived from. Pure
// math, no THREE/DOM dependency (Level 1 testable - see referencePlane.test.js).
//
// ── Why boundary-length-weighted moments, not plain vertex PCA ─────────────
//
// A user's digitized polygon can have an arbitrary number of extra,
// collinear vertices along any one edge (they clicked twice close together,
// or a capture tool inserted a midpoint). Plain PCA over the raw vertex
// array would let those "accidental" extra points silently outweigh a
// sparsely-clicked edge of the same real-world length - the fit would
// depend on how the user happened to click, not on the polygon's actual
// geometric boundary. That is unacceptable for a measurement tool.
//
// Instead, the boundary is treated as a continuous closed curve and the
// centroid/covariance are the *length-weighted line integrals* over that
// curve:
//
//   centroid   = (1/L) * ∮ p(s) ds
//   covariance = (1/L) * ∮ (p(s)-centroid)(p(s)-centroid)ᵀ ds
//
// Both integrals have an exact closed form per straight segment (derived
// below), so this is computed exactly, in O(n), with no resampling
// resolution parameter - and, being a genuine definite integral, it is
// *exactly* invariant to splitting a segment into collinear sub-segments
// (the integral over [a,b] equals the sum of the integrals over any
// partition of [a,b] - elementary integral additivity). This is verified
// empirically in referencePlane.test.js by densifying a polygon edge with
// extra collinear vertices and checking the fit is unchanged to float64
// precision.
//
// Per segment P0->P1, parametrised p(t) = P0 + t*d (d = P1-P0, t in [0,1]),
// length L = |d|, ds = L dt:
//
//   ∫ p ds            = L * (P0 + P1) / 2
//   ∫ (p-c)(p-c)ᵀ ds   = L * [ a⊗a + (a⊗d + d⊗a)/2 + d⊗d/3 ],  a = P0 - c
//
// (the second line is ∫₀¹ (a+td)(a+td)ᵀ dt, expanded and integrated term by
// term - a quadratic in t, so this is exact, not a numerical approximation).
//
// The plane normal is the eigenvector of the smallest eigenvalue of that
// covariance matrix (the direction the boundary varies least along) - the
// standard total-least-squares/orthogonal-regression plane fit, here
// applied to the boundary's own mass distribution instead of raw vertices.

function sub(a, b){ return {x: a.x - b.x, y: a.y - b.y, z: a.z - b.z}; }
function add(a, b){ return {x: a.x + b.x, y: a.y + b.y, z: a.z + b.z}; }
function scale(a, s){ return {x: a.x * s, y: a.y * s, z: a.z * s}; }
function dot(a, b){ return a.x * b.x + a.y * b.y + a.z * b.z; }
function cross(a, b){
	return {x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x};
}
function length(a){ return Math.sqrt(dot(a, a)); }
function normalize(a){
	const len = length(a);
	if(len < 1e-15) return {x: 0, y: 0, z: 0};
	return scale(a, 1 / len);
}

// symmetric outer-product accumulation helper: adds w * (a⊗b + b⊗a)/2-style
// terms into a flat 6-element upper-triangle [xx, xy, xz, yy, yz, zz]
function addOuter(M, a, b, w){
	M[0] += w * a.x * b.x;
	M[1] += w * (a.x * b.y + a.y * b.x) / 2;
	M[2] += w * (a.x * b.z + a.z * b.x) / 2;
	M[3] += w * a.y * b.y;
	M[4] += w * (a.y * b.z + a.z * b.y) / 2;
	M[5] += w * a.z * b.z;
}

// ── Cyclic Jacobi eigenvalue decomposition for a symmetric 3x3 matrix ──────
//
// Chosen over a closed-form cubic-root solution deliberately: Jacobi
// rotation is unconditionally numerically stable (no catastrophic
// cancellation near repeated/near-repeated eigenvalues, which a
// near-planar-but-not-quite boundary can easily produce), and is a fully
// deterministic function of the input matrix given a fixed pivot-selection
// rule (here: always rotate away the largest-magnitude off-diagonal entry).
// M: flat [xx, xy, xz, yy, yz, zz]. Returns {eigenvalues: [3], eigenvectors:
// [[3],[3],[3]]} - eigenvectors[i] is the unit eigenvector for eigenvalues[i],
// NOT sorted (caller sorts as needed).
export function jacobiEigenSymmetric3(M, maxSweeps = 100, tol = 1e-14){
	const A = [
		[M[0], M[1], M[2]],
		[M[1], M[3], M[4]],
		[M[2], M[4], M[5]],
	];
	const V = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

	for(let sweep = 0; sweep < maxSweeps; sweep++){
		let p = 0, q = 1, maxVal = Math.abs(A[0][1]);
		if(Math.abs(A[0][2]) > maxVal){ p = 0; q = 2; maxVal = Math.abs(A[0][2]); }
		if(Math.abs(A[1][2]) > maxVal){ p = 1; q = 2; maxVal = Math.abs(A[1][2]); }
		if(maxVal < tol) break;

		const app = A[p][p], aqq = A[q][q], apq = A[p][q];
		const phi = 0.5 * Math.atan2(2 * apq, aqq - app);
		const c = Math.cos(phi), s = Math.sin(phi);

		for(let k = 0; k < 3; k++){
			const akp = A[k][p], akq = A[k][q];
			A[k][p] = c * akp - s * akq;
			A[k][q] = s * akp + c * akq;
		}
		for(let k = 0; k < 3; k++){
			const apk = A[p][k], aqk = A[q][k];
			A[p][k] = c * apk - s * aqk;
			A[q][k] = s * apk + c * aqk;
		}
		for(let k = 0; k < 3; k++){
			const vkp = V[k][p], vkq = V[k][q];
			V[k][p] = c * vkp - s * vkq;
			V[k][q] = s * vkp + c * vkq;
		}
	}

	return {
		eigenvalues: [A[0][0], A[1][1], A[2][2]],
		eigenvectors: [
			[V[0][0], V[1][0], V[2][0]],
			[V[0][1], V[1][1], V[2][1]],
			[V[0][2], V[1][2], V[2][2]],
		],
	};
}

// ── Deterministic tangent frame from a unit normal ─────────────────────────
//
// (u,v) axes spanning the plane, chosen without reference to anything but
// `normal` itself (so the whole frame is a pure function of the fitted
// plane, never of e.g. polygon vertex order). worldZ is used as the
// reference "up" for u unless the plane is close to horizontal (normal
// close to worldZ), in which case worldX is used instead - the standard
// tangent-frame degeneracy fix, applied deterministically via a fixed
// threshold, not the current view/camera.
export function tangentFrame(normal){
	const worldZ = {x: 0, y: 0, z: 1};
	const worldX = {x: 1, y: 0, z: 0};
	const reference = Math.abs(dot(normal, worldZ)) > 0.9 ? worldX : worldZ;

	const uRaw = sub(reference, scale(normal, dot(reference, normal)));
	const uAxis = normalize(uRaw);
	const vAxis = normalize(cross(normal, uAxis));
	return {uAxis, vAxis};
}

// vertices: array of {x,y,z} - the polygon's UNIQUE vertex ring (NOT
// repeating the first vertex at the end; the boundary is treated as closed
// by wrapping index n-1 -> 0). Requires >= 3 vertices (caller validates via
// polygon2D.js before calling this).
//
// Returns:
//   origin    - {x,y,z}, the length-weighted boundary centroid (lies
//               exactly on the fitted plane by construction of PCA/TLS)
//   normal    - {x,y,z}, unit normal, oriented so dot(normal, worldZ) >= 0
//               whenever that is well-defined (see inclinationDeg)
//   uAxis/vAxis - {x,y,z}, unit, orthonormal, span the plane
//   eigenvalues - [smallest, mid, largest] of the boundary covariance -
//               smallest is the variance explained by `normal` itself
//               (small relative to the others => good, near-planar fit)
//   inclinationDeg - angle between `normal` and world-up, 0..90
//   residuals - {rms, medianAbs, max, perVertex: [n]} - orthogonal signed
//               distance of each ACTUAL polygon vertex to the fitted plane
//               (not the continuous boundary integral the fit itself used -
//               these are the intuitive, inspectable per-vertex figures a
//               user would expect from "how well does my clicked polygon
//               match a flat plane", explicitly documented as distinct from
//               the fit criterion - see this file's own header comment)
export function fitBoundaryPlane(vertices){
	const n = vertices.length;

	let totalLength = 0;
	let weightedSum = {x: 0, y: 0, z: 0};
	for(let i = 0; i < n; i++){
		const p0 = vertices[i], p1 = vertices[(i + 1) % n];
		const d = sub(p1, p0);
		const L = length(d);
		totalLength += L;
		weightedSum = add(weightedSum, scale(add(p0, p1), L / 2));
	}
	const origin = totalLength > 1e-12 ? scale(weightedSum, 1 / totalLength) : vertices[0];

	const M = [0, 0, 0, 0, 0, 0]; // xx, xy, xz, yy, yz, zz
	for(let i = 0; i < n; i++){
		const p0 = vertices[i], p1 = vertices[(i + 1) % n];
		const d = sub(p1, p0);
		const L = length(d);
		if(L < 1e-12) continue;
		const a = sub(p0, origin);
		addOuter(M, a, a, L);
		addOuter(M, a, d, L); // contributes the (a⊗d + d⊗a)/2 term via addOuter's own averaging
		addOuter(M, d, d, L / 3);
	}

	const {eigenvalues, eigenvectors} = jacobiEigenSymmetric3(M);
	let minIdx = 0;
	for(let i = 1; i < 3; i++) if(eigenvalues[i] < eigenvalues[minIdx]) minIdx = i;
	const order = [minIdx, ...[0, 1, 2].filter((i) => i !== minIdx)];
	const sortedEigenvalues = order.map((i) => eigenvalues[i]);

	let normal = normalize({x: eigenvectors[minIdx][0], y: eigenvectors[minIdx][1], z: eigenvectors[minIdx][2]});

	// deterministic sign: point the normal "up" whenever that's meaningful.
	// Near-vertical planes (dot ~ 0) have no well-defined "up" side - the
	// sign is still picked deterministically (>=0 tie-break), but
	// inclinationDeg is what callers should check before relying on the
	// up/down orientation meaning anything physically.
	if(dot(normal, {x: 0, y: 0, z: 1}) < 0) normal = scale(normal, -1);

	const inclinationDeg = (Math.acos(Math.min(1, Math.max(-1, Math.abs(normal.z)))) * 180) / Math.PI;

	const {uAxis, vAxis} = tangentFrame(normal);

	const perVertex = vertices.map((p) => dot(sub(p, origin), normal));
	const abs = perVertex.map(Math.abs);
	const sumSq = abs.reduce((s, v) => s + v * v, 0);
	const rms = Math.sqrt(sumSq / n);
	const sortedAbs = [...abs].sort((a, b) => a - b);
	const medianAbs = n % 2 === 1
		? sortedAbs[(n - 1) / 2]
		: (sortedAbs[n / 2 - 1] + sortedAbs[n / 2]) / 2;
	const max = Math.max(...abs);

	return {
		origin,
		normal,
		uAxis,
		vAxis,
		eigenvalues: sortedEigenvalues,
		inclinationDeg,
		residuals: {rms, medianAbs, max, perVertex},
	};
}

// World point -> local (u,v,w) relative to a fitted frame (see
// fitBoundaryPlane). w is the signed orthogonal distance to the plane
// (w>0 = above/fill side, w<0 = below/cut side, per the tool's own
// convention - see signedVolume.js).
export function toLocal(point, frame){
	const rel = sub(point, frame.origin);
	return {u: dot(rel, frame.uAxis), v: dot(rel, frame.vAxis), w: dot(rel, frame.normal)};
}

export function projectPolygonToLocal(vertices, frame){
	return vertices.map((p) => toLocal(p, frame));
}
